/**
 * EditUserProfilePage
 *
 * Allows the current user to edit their profile information.
 * Matches Flutter's EditUserProfileScreen (edit mode only).
 *
 * The bar is the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`), the web port of
 * Flutter's shared page-bar shape (`base_app_bar.dart:5-42`). It matches
 * `EditUserProfileScreen.appBar()` (`edit_user_profile_screen.dart:561-584`):
 * a back-chevron leading (edit mode only — the only mode the web ports) over a
 * `centerTitle: true` title, with **no** actions; Flutter's "Done" control is a
 * body button (`publishButton()`), which the web mirrors as the bottom button
 * block below.
 *
 * Back is the deep-link-guarded `Navigator.goBackOr` (`presentation.md` → App bar
 * → "Back affordance"), a **superset** of Flutter's `_onBackPress` →
 * `NavigationService.pop()` (`edit_user_profile_screen.dart:159-162`), not a
 * divergence from it: whenever there is in-app history the two pop identically,
 * and they differ only on the entry Flutter cannot have at all — a refreshed or
 * pasted `/profile/edit` URL, where an unguarded pop leaves a dead chevron.
 * (Flutter's pop also carries `result: _dataUpdated`; the web has no pop-result
 * channel and never had one here, so that half is unchanged by this guard.)
 *
 * Moving from the hand-rolled bar to `ExpauseAppBar` makes three intentional
 * visual changes: the title steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and the top safe-area inset is now honoured.
 *
 * Architecture: Component → Hook → UseCase → Service
 */

import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { Navigator } from '@/presentation/routing/Navigator';
import { useEditUserProfile } from './hooks/useEditUserProfile';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import {
  ExpauseButton,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { AddContentSheet } from '@/presentation/feature/publish/components/sheets/addContent/AddContentSheet';
import { canOpenAddContent } from '@/presentation/feature/publish/utils/canOpenAddContent';
import { EditProfileImageSection } from './components/EditProfileImageSection';
import { EditProfileBasicInfoSection } from './components/EditProfileBasicInfoSection';
import { EditProfileSubscriptionSection } from './components/EditProfileSubscriptionSection';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const EditUserProfilePage = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const location = useLocation();

  // Auth gate — the page owns the `currentUser` selector (`presentation.md` →
  // page + feature-hook split). Only used for the back handler's deep-link
  // fallback; the form's own copy lives in `useEditUserProfile`.
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  const {
    profileImage,
    name,
    username,
    bio,
    subscriptionCharge,
    isGeneratingUsername,
    usernameExists,
    isSaving,
    canSave,
    setName,
    setUsername,
    setBio,
    setSubscriptionCharge,
    onNameBlur,
    onUsernameBlur,
    onProfileImagePicked,
    handleSubmit,
  } = useEditUserProfile(screenKey);

  useBasePage({
    screenKey,
    onInit: () => { },
    onPush: () => { },
  });

  // Deep-link-guarded back. With in-app history this is Flutter's `_onBackPress`
  // (`:159-162`) exactly; with none — a refreshed / pasted `/profile/edit` — it
  // lands on the signed-in user's own profile (`/:userId`), the surface this
  // route is conceptually nested under and the one the edit ultimately updates.
  // `currentUser` can still be null while auth resolves, so the Feed is the
  // second-level fallback (same shape as `UserVideosPage`'s param-guarded one).
  const handleBack = useCallback(() => {
    Navigator.goBackOr(() =>
      currentUser
        ? Navigator.navigateToUserDetails(currentUser.firebaseId)
        : Navigator.navigateToFeed(),
    );
  }, [currentUser]);

  // Flutter `onChangeImageTapped` (`edit_user_profile_screen.dart:270-286`). The
  // page owns the sheet invocation — a feature hook never opens one.
  //
  // Flutter's `:271` `hideKeyboard()` needs no web port: clicking the avatar blurs
  // the active input, so onNameBlur / onUsernameBlur fire exactly as Flutter's
  // focus listeners (`:164-185`) do. A cancelled pick never calls `onPickImage` —
  // the no-op parity of `result == null` (`:280`). The seam also carries
  // `aspectRatio`, unused here because the avatar is a fixed circle.
  //
  // The upload-in-progress guard is the first thing Flutter's shared
  // `navigateToAddContentScreen` runs (`navigation_service.dart:420-435`), and
  // this avatar picker is one of its call sites
  // (`edit_user_profile_screen.dart:272`), so it is gated here too: while a video
  // upload is running the info sheet shows and no picker opens.
  const handleChangeImage = useCallback(() => {
    if (!canOpenAddContent({ l10n, screenKey })) return;
    AddContentSheet.show({
      screenKey,
      width: responsive.addContentSheet.width,
      fixedHeight: responsive.addContentSheet.fixedHeight,
      profileImage: { onPickImage: ({ file }) => onProfileImagePicked(file) },
    });
  }, [l10n, screenKey, responsive, onProfileImagePicked]);

  return (
    <div
      {...qaAttr({ id: 'edit-profile', status: isSaving ? 'saving' : 'ready' })}
      style={{
        minHeight: '100vh',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* App Bar — back leading + centered title, no actions (Flutter
          `appBar()` / `appBarLeadingWidget()`, `:561-584`). */}
      <ExpauseAppBar
        rootQa={{ id: 'edit-profile-app-bar' }}
        title={l10n.editUserProfile.title}
        leadingQa={{ id: 'edit-profile-back' }}
        onLeadingTap={handleBack}
      />

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: `${responsive.spacing.base}px`,
          paddingBottom: `${responsive.spacing['2xl']}px`,
        }}
      >
        {/* Profile image (Flutter `profileIcon()`, first in the scroll body — `:472-476`) */}
        <EditProfileImageSection
          profileImage={profileImage}
          onChangeImage={handleChangeImage}
        />

        <EditProfileBasicInfoSection
          name={name}
          username={username}
          bio={bio}
          isGeneratingUsername={isGeneratingUsername}
          usernameExists={usernameExists}
          onNameChange={setName}
          onUsernameChange={setUsername}
          onBioChange={setBio}
          onNameBlur={onNameBlur}
          onUsernameBlur={onUsernameBlur}
        />

        <EditProfileSubscriptionSection
          subscriptionCharge={subscriptionCharge}
          onSubscriptionChargeChange={setSubscriptionCharge}
        />
      </div>

      {/* Done button - hidden when keyboard is open (always shown on web) */}
      <div
        style={{
          padding: `${responsive.spacing.base}px`,
          paddingBottom: responsive.spacing.lg,
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: theme.background,
        }}
      >
        {/*
          Flutter's `publishButton()` (`:507-532`): no background fill, a hairline
          border that turns `exclusive` once the form is saveable, and deliberately
          wide horizontal padding (`:519-521`). `secondary` is the only bordered
          variant, so the fill is neutralised to `transparent` and the border color
          driven off `canSave`. Disabled dimming/pointer handling comes from the
          component's own `disabled` styling — not hand-rolled here.
        */}
        <ExpauseButton
          {...qaAttr({ id: 'edit-profile-save' })}
          variant={ButtonVariant.secondary}
          onClick={handleSubmit}
          disabled={!canSave || isSaving}
          style={{
            padding: responsive.button.padding.xl,
          }}
        >
          {l10n.editUserProfile.doneButton}
        </ExpauseButton>
      </div>

      {/* Loading overlay when saving */}
      {isSaving && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backdropFilter: 'blur(4px)',
            backgroundColor: theme.loadingOverlay,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <ExpauseLoadingIndicator size={40} />
        </div>
      )}
    </div>
  );
};
